/**
 * Add-Hedge Sheet — opened from AccountPairDetail's header button.
 *
 * Steps:
 *   1. Operator picks / types a symbol.
 *   2. Clicks Validate — we round-trip both legs through the bot
 *      (POST /api/account-pairs/:slug/validate-symbol). UI shows a
 *      spinner then per-leg result cards.
 *   3. On validation success, Lots input is pre-filled with safeLots
 *      (min of each leg's maxLot) if present; operator can edit down.
 *   4. Create Hedge submits — this also add-allowlists the symbol if
 *      it wasn't already there.
 *
 * Validation FAILURE on either leg blocks Create Hedge. Warnings (eg.
 * leverage differs, specs unknown) show but don't block.
 */

import { useEffect, useState, type ReactNode } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "@/components/ui/icons";
import {
  updateAllowlist,
  addSymbolPair,
} from "@/lib/api/account-pairs";
import { validateSymbol, type ValidateSymbolResult } from "@/lib/api/pair-proposals";
import { extractApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountPairSlug: string;
  allowlist: string[];
  defaultLots: number;
  onAllowlistChange: (next: string[]) => void;
  onHedgeCreated: () => void;
  longAccountLabel: string;
  shortAccountLabel: string;
}

const PREFERRED_SYMBOLS = [
  "XAUUSD",
  "XAGUSD",
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
] as const;

function SpecRow({ label, value, warning }: { label: string; value: string; warning?: boolean }): ReactNode {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-mono", warning && "text-amber-500")}>{value}</span>
    </div>
  );
}

function LegCard({
  title,
  leg,
}: {
  title: string;
  leg: ValidateSymbolResult["long"];
}): ReactNode {
  if (leg.status === "pending") {
    return (
      <div className="rounded-lg border border-border p-3">
        <p className="font-sans text-xs font-medium text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">Validating…</p>
      </div>
    );
  }
  if (leg.status === "timeout" || leg.status === "missing") {
    return (
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
        <p className="font-sans text-xs font-medium text-foreground">{title}</p>
        <p className="mt-1 text-xs text-amber-500">
          {leg.error ?? "Bot did not respond — is it online?"}
        </p>
      </div>
    );
  }
  if (!leg.ok) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
        <p className="font-sans text-xs font-medium text-foreground">{title}</p>
        <p className="mt-1 text-xs text-destructive">
          {leg.error ?? "Symbol not tradable on this broker."}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3">
      <p className="font-sans text-xs font-medium text-foreground">{title}</p>
      <p className="mt-1 text-xs text-emerald-500">
        Tradable as {leg.resolvedSymbol ?? "—"}
      </p>
      <div className="mt-2 flex flex-col gap-1">
        <SpecRow
          label="Leverage"
          value={leg.leverage != null ? `1:${leg.leverage}` : "Unknown"}
          warning={leg.leverage == null}
        />
        <SpecRow
          label="Contract Size"
          value={leg.contractSize != null ? leg.contractSize.toLocaleString() : "Unknown"}
          warning={leg.contractSize == null}
        />
        <SpecRow
          label="Min Lot"
          value={leg.minLot != null ? String(leg.minLot) : "Unknown"}
          warning={leg.minLot == null}
        />
        <SpecRow
          label="Max Lot"
          value={leg.maxLot != null ? String(leg.maxLot) : "Unknown"}
          warning={leg.maxLot == null}
        />
        <SpecRow
          label="Spread"
          value={leg.spreadPips != null ? `${leg.spreadPips} pips` : "Unknown"}
          warning={leg.spreadPips == null}
        />
      </div>
    </div>
  );
}

export function AddHedgeSheet({
  open,
  onOpenChange,
  accountPairSlug,
  allowlist,
  defaultLots,
  onAllowlistChange,
  onHedgeCreated,
  longAccountLabel,
  shortAccountLabel,
}: Props): ReactNode {
  const [symbol, setSymbol] = useState("XAUUSD");
  const [lots, setLots] = useState(String(defaultLots));
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidateSymbolResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normSymbol = symbol.trim().toUpperCase();
  const canValidate = normSymbol.length > 0 && !validating && !submitting;
  const canSubmit =
    validation?.ok === true &&
    normSymbol.length > 0 &&
    Number(lots) > 0 &&
    !submitting;

  useEffect(() => {
    if (!open) {
      setLots(String(defaultLots));
    }
  }, [defaultLots, open]);

  const reset = (): void => {
    setSymbol("XAUUSD");
    setLots(String(defaultLots));
    setValidation(null);
    setError(null);
    setValidating(false);
    setSubmitting(false);
  };

  const handleValidate = async (): Promise<void> => {
    setValidating(true);
    setValidation(null);
    setError(null);
    try {
      const res = await validateSymbol(accountPairSlug, normSymbol);
      setValidation(res);
      if (res.safeLots != null) {
        setLots(String(res.safeLots));
      }
    } catch (e) {
      setError(extractApiErrorMessage(e));
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      // Ensure the symbol is on the allowlist before creating the hedge —
      // the POST /:slug/symbols endpoint rejects off-allowlist symbols.
      if (!allowlist.includes(normSymbol)) {
        const nextList = Array.from(new Set([...allowlist, normSymbol]));
        const saved = await updateAllowlist(accountPairSlug, nextList);
        onAllowlistChange(saved);
      }
      // Forward spec from the preceding validate round-trip so the
      // pair row gets populated with contractSize + leverage. Each leg
      // reported its own — if they match we take one; if they differ
      // (unusual), prefer the short leg's spec since that's the one
      // more often margin-constrained.
      const longOk = validation?.long.ok ? validation.long : null;
      const shortOk = validation?.short.ok ? validation.short : null;
      const contractSize =
        shortOk?.contractSize ?? longOk?.contractSize ?? null;
      const leverage = shortOk?.leverage ?? longOk?.leverage ?? null;
      await addSymbolPair(accountPairSlug, {
        symbol: normSymbol,
        lots: Number(lots),
        contractSize,
        leverage,
      });
      onHedgeCreated();
      reset();
      onOpenChange(false);
    } catch (e) {
      setError(extractApiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const preferred = PREFERRED_SYMBOLS.filter((sym) => sym === normSymbol || !allowlist.includes(sym));

  return (
    <Sheet open={open} onOpenChange={(next) => {
      if (!next) reset();
      onOpenChange(next);
    }}>
      <SheetContent side="right" className="w-full sm:max-w-md font-sans">
        <SheetHeader>
          <SheetTitle>Add A Symbol Hedge</SheetTitle>
          <SheetDescription>
            Validate the symbol on both brokers before adding a hedge.
            New pairs start paused for setup, so nothing trades until you
            explicitly resume the pair.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium capitalize text-muted-foreground">
              Symbol
            </label>
            <div className="flex items-center gap-2">
              <Input
                value={symbol}
                onChange={(e) => {
                  setSymbol(e.target.value);
                  setValidation(null);
                }}
                placeholder="XAUUSD"
                autoComplete="off"
                spellCheck={false}
                aria-label="Symbol to validate"
                className="uppercase font-mono"
                disabled={submitting}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!canValidate}
                aria-busy={validating}
                onClick={() => {
                  void handleValidate();
                }}
              >
                {validating && (
                  <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
                )}
                {validating ? "Validating…" : "Validate"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {preferred.map((sym) => (
                <Button
                  key={sym}
                  type="button"
                  size="sm"
                  variant={sym === normSymbol ? "default" : "outline"}
                  className="h-7 px-2 font-mono text-[11px]"
                  disabled={submitting}
                  onClick={() => {
                    setSymbol(sym);
                    setValidation(null);
                  }}
                >
                  {sym}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Index symbols vary by broker. Add those using the broker’s own symbol and validate them before trading.
            </p>
            {allowlist.length > 0 && !allowlist.includes(normSymbol) && normSymbol && (
              <p className="text-xs text-muted-foreground">
                Not on the allowlist yet — will be added on create.
              </p>
            )}
          </div>

          {validation && (
            <div className="flex flex-col gap-3">
              <LegCard title={longAccountLabel} leg={validation.long} />
              <LegCard title={shortAccountLabel} leg={validation.short} />
              {validation.safeLots != null && (
                <p className="text-xs text-muted-foreground">
                  Recommended starting lots on this pair:{" "}
                  <span className="font-mono text-foreground">{validation.safeLots}</span>
                </p>
              )}
              {!validation.ok && (
                <Badge variant="destructive" className="self-start">
                  Cannot add — validation failed on one or both legs
                </Badge>
              )}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium capitalize text-muted-foreground">
              Lots
            </label>
            <Input
              value={lots}
              onChange={(e) => setLots(e.target.value)}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              aria-label="Lots per leg"
              className="font-mono tabular-nums"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Default follows account size. A 5k pair starts at{" "}
              <span className="font-mono">0.05</span>, then validation and
              live margin checks can size it down.
            </p>
          </div>

          {error && (
            <p role="alert" aria-live="polite" className="text-xs text-destructive">{error}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleSubmit();
              }}
              disabled={!canSubmit}
              aria-busy={submitting}
            >
              {submitting && (
                <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
              )}
              {submitting ? "Creating…" : "Create Hedge"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
